import React, { useEffect, useState } from "react";
import { useT } from "../i18n";

/**
 * Free-typing date field that adapts to the current i18n locale:
 *   - Italian (it):     DD/MM/YYYY
 *   - English US (en):  MM/DD/YYYY
 * Exposes ISO YYYY-MM-DD via onChange.
 */
function isoToParts(iso) {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return { y: m[1], mo: m[2], d: m[3] };
}

function isoToDisplay(iso, lang) {
  const p = isoToParts(iso);
  if (!p) return "";
  return lang === "en" ? `${p.mo}/${p.d}/${p.y}` : `${p.d}/${p.mo}/${p.y}`;
}

function displayToISO(s, lang) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const [, a, b, yyyy] = m;
  // it: a=dd, b=mm  |  en: a=mm, b=dd
  const dd = lang === "en" ? Number(b) : Number(a);
  const mo = lang === "en" ? Number(a) : Number(b);
  const y = Number(yyyy);
  if (mo < 1 || mo > 12) return null;
  if (dd < 1 || dd > 31) return null;
  const dt = new Date(Date.UTC(y, mo - 1, dd));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== dd) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${y}-${pad(mo)}-${pad(dd)}`;
}

function autoSlash(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  let out = digits;
  if (digits.length > 4) {
    out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  } else if (digits.length > 2) {
    out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return out;
}

export function DateField({ value, onChange, testId, className = "" }) {
  const { lang } = useT();
  const [text, setText] = useState(() => isoToDisplay(value, lang));

  useEffect(() => { setText(isoToDisplay(value, lang)); }, [value, lang]);

  const commit = (raw) => {
    const formatted = autoSlash(raw);
    setText(formatted);
    if (formatted.length === 10) {
      const iso = displayToISO(formatted, lang);
      if (iso) onChange(iso);
    } else if (formatted.length === 0) {
      onChange("");
    }
  };

  const placeholder = lang === "en" ? "MM/DD/YYYY" : "GG/MM/AAAA";

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={placeholder}
      value={text}
      maxLength={10}
      onChange={(e) => commit(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      data-testid={testId}
      className={`w-full h-12 rounded-xl bg-zinc-50 border border-zinc-200 px-4 text-base font-mono tracking-wider focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950 outline-none ${className}`}
    />
  );
}

export default DateField;
